import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../../context';
import { parseRoles, canAccess } from '../../utils/rbac';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import Modal from '../shared/Modal';
import RaidQuickPanel from './RaidQuickPanel';
import { formatDate, formatMoney } from '../../utils/format';
import { MILESTONE_STATUS_TRANSITIONS } from '../../utils/milestoneStatus';

// ── Constants ────────────────────────────────────────────────────────────────
// RAG and delivery status are tracked ONCE PER PROJECT (SOW), not per
// milestone — the same model ProjectUpdatePage.jsx uses (which replaced the
// old per-milestone Project Health screen). Milestones carry their own dates,
// billing status, summary and dependency — never their own RAG.
const RAG_OPTS = [
  { value: 'green', label: 'Green', dot: '#10B981', bg: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'amber', label: 'Amber', dot: '#F59E0B', bg: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'red',   label: 'Red',   dot: '#EF4444', bg: 'bg-red-100 text-red-700 border-red-300' },
];
const RAG_DOT = { green: '#10B981', amber: '#F59E0B', red: '#EF4444' };
const RAG_RANK = { red: 3, amber: 2, green: 1 };

// The project RAG is derived from delivery status — it is not set by hand — so
// the Cockpit, Project Update, Project Health and PMO Dashboard can never
// disagree. Mirrors STATUS_RAG_COLOR in HealthPage and scheduleRagFromDelivery
// on the server: blocked/delayed = red, at-risk/on-hold = amber, else green.
const STATUS_RAG = { on_track: 'green', at_risk: 'amber', delayed: 'red', blocked: 'red', on_hold: 'amber', uat: 'green', complete: 'green' };
const ragFromDelivery = (s) => STATUS_RAG[s] || 'green';
// Overall RAG = worst of the four dimensions (never picked by hand).
const worstRag = (...rags) => rags.includes('red') ? 'red' : rags.includes('amber') ? 'amber' : 'green';

const DELIVERY_STATUSES = [
  { value: 'on_track', label: 'On Track', bg: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'at_risk',  label: 'At Risk',  bg: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'delayed',  label: 'Delayed',  bg: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'blocked',  label: 'Blocked',  bg: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'on_hold',  label: 'On Hold',  bg: 'bg-gray-100 text-gray-600 border-gray-300' },
  { value: 'uat',      label: 'UAT',      bg: 'bg-purple-100 text-purple-700 border-purple-300' },
  { value: 'complete', label: 'Complete', bg: 'bg-teal-100 text-teal-700 border-teal-300' },
];
const dsMeta = v => DELIVERY_STATUSES.find(s => s.value === v) || DELIVERY_STATUSES[0];

// Terminal/finance statuses stay read-only; everything else follows the
// shared MILESTONE_STATUS_TRANSITIONS map (same rules as the SOW Milestones tab).
const TERMINAL = ['Invoiced', 'Paid', 'Complete', 'Completed', 'Credit Noted'];
const DONEISH = ['Ready to bill', 'Ready To Bill', 'Invoiced', 'Paid', 'Complete', 'Completed', 'Credit Noted'];
const isDoneish = (status) => DONEISH.includes(status);
const todayStr = () => new Date().toISOString().split('T')[0];

// ── Project-level status/RAG update ─────────────────────────────────────────
function ProjectStatusModal({ sowId, sowRef, sow, latest, history, onClose, onSaved }) {
  const { toast } = useToast();
  const [deliveryStatus, setDeliveryStatus] = useState(latest?.delivery_status || 'on_track');
  // Schedule is derived from delivery status; budget/resource/risk are PM-set;
  // overall is the worst of the four (never picked by hand).
  const scheduleRag = ragFromDelivery(deliveryStatus);
  const [budgetRag, setBudgetRag]     = useState(latest?.budget_rag   || 'green');
  const [resourceRag, setResourceRag] = useState(latest?.resource_rag || 'green');
  const [riskRag, setRiskRag]         = useState(latest?.risk_rag     || 'green');
  const overallRag = worstRag(scheduleRag, budgetRag, resourceRag, riskRag);
  const [recentProgress, setRecentProgress] = useState(latest?.recent_progress || '');
  const [nextPeriod, setNextPeriod]         = useState(latest?.next_period || '');
  const [risksIssues, setRisksIssues]       = useState(latest?.risks_issues || '');
  // RR (Rolls-Royce) actions & comment are project (SOW) level, not part of the
  // weekly RAG snapshot — saved straight onto the SOW.
  const [rrActions, setRrActions]           = useState(sow?.rr_actions || '');
  const [rrComment, setRrComment]           = useState(sow?.rr_comment || '');
  const [saving, setSaving]                 = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`project-updates/${sowId}`, {
        delivery_status: deliveryStatus, overall_rag: overallRag,
        budget_rag: budgetRag, resource_rag: resourceRag, risk_rag: riskRag,
        recent_progress: recentProgress || null, next_period: nextPeriod || null, risks_issues: risksIssues || null,
        week_commencing: todayStr(),
      });
      if ((rrActions || '') !== (sow?.rr_actions || '') || (rrComment || '') !== (sow?.rr_comment || '')) {
        await api.put(`sows/${sowId}`, { rr_actions: rrActions || null, rr_comment: rrComment || null });
      }
      toast.success('Project update saved');
      onSaved();
    } catch (e) {
      toast.error('Failed to save: ' + (e.message || 'unknown error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Update project — ${sowRef}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
          <button onClick={save} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-60">{saving ? 'Saving…' : 'Save update'}</button>
        </>
      }
    >
      <div className="space-y-3 text-[10px]">
        <div>
          <span className="text-[8px] font-bold uppercase text-gray-500 mb-1 block">Delivery status</span>
          <div className="flex flex-wrap gap-1.5">
            {DELIVERY_STATUSES.map(s => (
              <button key={s.value} type="button" onClick={() => setDeliveryStatus(s.value)}
                className={`text-[9px] px-2.5 py-1 rounded-full border font-semibold ${deliveryStatus === s.value ? s.bg : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[8px] font-bold uppercase text-gray-500">RAG status</span>
            {(() => {
              const r = RAG_OPTS.find(o => o.value === overallRag) || RAG_OPTS[0];
              return (
                <span className="text-[8px] text-gray-500 inline-flex items-center gap-1">Overall
                  <span className={`px-2 py-0.5 rounded border font-semibold inline-flex items-center gap-1 ${r.bg}`}>
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: r.dot }} />{r.label}
                  </span>
                </span>
              );
            })()}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {/* Schedule — read-only, derived from delivery status */}
            <div>
              <span className="text-[8px] font-semibold text-gray-400 block mb-0.5">Schedule <span className="normal-case">(auto)</span></span>
              {(() => {
                const r = RAG_OPTS.find(o => o.value === scheduleRag) || RAG_OPTS[0];
                return (
                  <span className={`w-full text-[9px] px-2 py-1 rounded border font-semibold inline-flex items-center justify-center gap-1 ${r.bg}`}
                    title="Set automatically from delivery status">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: r.dot }} />{r.label}
                  </span>
                );
              })()}
            </div>
            {/* Budget / Resource / Risk — PM-set */}
            {[['Budget', budgetRag, setBudgetRag], ['Resource', resourceRag, setResourceRag], ['Risk', riskRag, setRiskRag]].map(([label, val, set]) => (
              <div key={label}>
                <span className="text-[8px] font-semibold text-gray-400 block mb-0.5">{label}</span>
                <div className="flex gap-0.5">
                  {RAG_OPTS.map(r => (
                    <button key={r.value} type="button" onClick={() => set(r.value)}
                      title={r.label}
                      className={`flex-1 text-[9px] px-1 py-1 rounded border font-semibold inline-flex items-center justify-center ${val === r.value ? r.bg : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50'}`}>
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: r.dot }} />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        {[['Recent progress', recentProgress, setRecentProgress], ['Next period', nextPeriod, setNextPeriod], ['Risks & issues', risksIssues, setRisksIssues]].map(([label, val, set]) => (
          <label key={label} className="block">
            <span className="text-[8px] font-bold uppercase text-gray-500">{label}</span>
            <textarea className="form-textarea w-full text-[9px]" rows="2" value={val} onChange={e => set(e.target.value)} />
          </label>
        ))}
        <div className="pt-2 border-t border-gray-100 space-y-3">
          <div className="text-[8px] font-bold uppercase text-gray-400">RR (customer) — project level</div>
          {[['RR actions', rrActions, setRrActions], ['RR comment', rrComment, setRrComment]].map(([label, val, set]) => (
            <label key={label} className="block">
              <span className="text-[8px] font-bold uppercase text-gray-500">{label}</span>
              <textarea className="form-textarea w-full text-[9px]" rows="2" value={val} onChange={e => set(e.target.value)} />
            </label>
          ))}
        </div>

        <div className="pt-2 border-t border-gray-100">
          <RaidQuickPanel sowId={sowId} title="RAID (this project)" />
        </div>

        {history?.filter(h => h.id !== latest?.id).length > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <div className="text-[8px] font-bold uppercase text-gray-400 mb-1.5">Previous updates — what was said last time</div>
            <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
              {history.filter(h => h.id !== latest?.id).map(h => (
                <div key={h.id} className="flex items-start gap-2 text-[9px] border-b border-gray-50 pb-1.5 last:border-0">
                  {h.overall_rag && <span className="inline-block w-2 h-2 rounded-full mt-1 shrink-0" style={{ background: RAG_DOT[h.overall_rag] }} />}
                  <div className="min-w-0">
                    <div className="text-gray-500">{h.week_commencing ? formatDate(h.week_commencing) : '—'} · {dsMeta(h.delivery_status).label} · {h.updated_by_name || ''}</div>
                    {h.recent_progress && <div className="text-gray-600">{h.recent_progress}</div>}
                    {h.next_period && <div className="text-gray-400">Next: {h.next_period}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Milestone update (dates/status/summary/dependency — no RAG) ────────────────
function MilestoneModal({ sow, milestone, depReasons, missedReasons, isPmo, onClose, onSaved }) {
  const { toast } = useToast();
  const origContractDate = milestone.contract_date || '';
  const origStatus       = milestone.status || 'Awaiting';

  const [contractDate, setContractDate] = useState(origContractDate);
  const [forecastDate, setForecastDate] = useState(milestone.forecast_date || '');
  const [status, setStatus]             = useState(origStatus);
  const [summary, setSummary]           = useState(milestone.summary || '');
  const [missedReason, setMissedReason] = useState(milestone.missed_milestone_reason || '');
  const [depReason, setDepReason]       = useState(milestone.dependency_reason || '');
  const [depComment, setDepComment]     = useState(milestone.dependency_comment || '');
  const [sesNumber, setSesNumber]       = useState(milestone.ses_number || '');
  const [evidenceTitle, setEvidenceTitle] = useState('');
  const [evidenceUrl, setEvidenceUrl]     = useState('');
  const [saving, setSaving]             = useState(false);

  const contractChanged = contractDate !== origContractDate;
  const terminal  = TERMINAL.includes(status);
  // Invoiced/Paid/etc are already terminal; "Ready to bill" isn't terminal
  // (it can still move to Invoiced) but is just as done from a "what's
  // blocking this" standpoint — nothing left to summarise or unblock.
  const wrappedUp = terminal || status === 'Ready to bill';
  const isMissed  = forecastDate && forecastDate < todayStr() && ![...TERMINAL, 'Ready to bill', 'Ready To Bill'].includes(status);
  const statusOpts = MILESTONE_STATUS_TRANSITIONS[origStatus] || [origStatus];
  const needsSes  = !!sow.ses_applicable && status === 'Ready to bill' && origStatus !== 'Ready to bill';

  const save = async () => {
    if (isMissed && !missedReason) { toast.error('Select a missed-milestone reason — this milestone is past its forecast date.'); return; }
    if (contractChanged && (!evidenceTitle.trim() || !evidenceUrl.trim())) { toast.error('Evidence (title + link) is required for a contract date change.'); return; }
    if (needsSes && !sesNumber.trim()) { toast.error('An SES number is required to mark Ready to Bill.'); return; }
    setSaving(true);
    try {
      const payload = {};
      if (contractDate !== origContractDate) payload.contract_date = contractDate || null;
      if (forecastDate !== (milestone.forecast_date || '')) payload.forecast_date = forecastDate || null;
      if (status !== origStatus) payload.status = status;
      if (summary !== (milestone.summary || '')) payload.summary = summary || null;
      if (depReason !== (milestone.dependency_reason || '')) payload.dependency_reason = depReason || null;
      if (depComment !== (milestone.dependency_comment || '')) payload.dependency_comment = depComment || null;
      if (missedReason !== (milestone.missed_milestone_reason || '')) payload.missed_milestone_reason = missedReason || null;
      if (sesNumber.trim() && sesNumber.trim() !== (milestone.ses_number || '')) payload.ses_number = sesNumber.trim();
      if (Object.keys(payload).length) await api.put(`milestones/${milestone.id}`, payload);
      if (contractChanged) {
        await api.post(`milestones/${milestone.id}/documents`, {
          document_type: 'contract_date_change', title: evidenceTitle, url: evidenceUrl,
          notes: `Contract date changed to ${contractDate} via Delivery Cockpit`,
        });
      }
      toast.success('Milestone updated');
      onSaved();
    } catch (e) {
      toast.error('Failed to save: ' + (e.message || 'unknown error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Update — ${sow.reference} · ${milestone.milestone_code || ''}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
          <button onClick={save} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-60">{saving ? 'Saving…' : 'Save update'}</button>
        </>
      }
    >
      <div className="space-y-3 text-[10px]">
        {milestone.description && <div className="text-gray-500">{milestone.description}</div>}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2 bg-gray-50 rounded">
          <label className="block">
            <span className="text-[8px] font-bold uppercase text-gray-500">Contract date {!isPmo && <span className="normal-case text-gray-400">· PMO</span>}</span>
            {isPmo ? (
              <input type="date" className="form-input w-full text-[9px]" value={contractDate} onChange={e => setContractDate(e.target.value)} />
            ) : (
              <div className="py-1 text-gray-700" title="Contract date — changed by PMO via a CCN">{contractDate ? formatDate(contractDate) : '—'}</div>
            )}
          </label>
          <label className="block">
            <span className="text-[8px] font-bold uppercase text-gray-500">Forecast date</span>
            <input type="date" className="form-input w-full text-[9px]" value={forecastDate} onChange={e => setForecastDate(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-[8px] font-bold uppercase text-gray-500">Status</span>
            {terminal ? <div className="py-1 text-gray-700 font-semibold">{status}</div> : (
              <select className="form-select w-full text-[9px]" value={status} onChange={e => setStatus(e.target.value)}>
                {statusOpts.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </label>
          {needsSes && (
            <label className="block">
              <span className="text-[8px] font-bold uppercase text-red-600">SES number *</span>
              <input className="form-input w-full text-[9px]" value={sesNumber} onChange={e => setSesNumber(e.target.value)} placeholder="Required" />
            </label>
          )}
        </div>

        {!wrappedUp && (
          <label className="block">
            <span className="text-[8px] font-bold uppercase text-gray-500">Summary</span>
            <input className="form-input w-full text-[9px]" placeholder="Short status for this milestone…" value={summary} onChange={e => setSummary(e.target.value)} />
          </label>
        )}

        {isMissed && (
          <div className="p-2 bg-amber-50 border border-amber-200 rounded">
            <label className="block">
              <span className="text-[8px] font-bold uppercase text-amber-700">Missed-milestone reason — past forecast date <span className="text-red-500">*</span></span>
              <select className="form-select w-full text-[9px] mt-1" value={missedReason} onChange={e => setMissedReason(e.target.value)}>
                <option value="">— select —</option>
                {missedReasons.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}
              </select>
            </label>
          </div>
        )}

        {!wrappedUp && (
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[8px] font-bold uppercase text-gray-500">Dependency / blocker</span>
              <select className="form-select w-full text-[9px]" value={depReason} onChange={e => setDepReason(e.target.value)}>
                <option value="">— none —</option>
                {depReasons.map(o => <option key={o.id} value={o.value}>{o.label || o.value}</option>)}
              </select>
            </label>
            {depReason && (
              <label className="block">
                <span className="text-[8px] font-bold uppercase text-gray-500">Dependency detail</span>
                <input className="form-input w-full text-[9px]" placeholder={depReason === '3rd Party' ? "Name the third party and what it's waiting on" : "What's it waiting on?"} value={depComment} onChange={e => setDepComment(e.target.value)} />
              </label>
            )}
          </div>
        )}

        {contractChanged && (
          <div className="p-2 bg-blue-50 border border-blue-200 rounded grid grid-cols-2 gap-2">
            <div className="col-span-2 text-[8px] font-bold text-blue-700">Contract date changed — evidence required <span className="text-red-500">*</span></div>
            <input className="form-input w-full text-[9px]" placeholder="Evidence title (e.g. CCN-12)" value={evidenceTitle} onChange={e => setEvidenceTitle(e.target.value)} />
            <input className="form-input w-full text-[9px]" placeholder="Link / document path" value={evidenceUrl} onChange={e => setEvidenceUrl(e.target.value)} />
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Risks + Tasks side panels (lazy per SOW) ──────────────────────────────────
function RisksPanel({ sowId }) {
  const { toast } = useToast();
  const [risks, setRisks] = useState(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const load = () => api.get('risks', { sow_id: sowId, open_only: '1' }).then(d => setRisks(Array.isArray(d) ? d : [])).catch(() => setRisks([]));
  useEffect(() => { load(); }, [sowId]);
  const add = async () => {
    if (!title.trim()) return;
    try { await api.post('risks', { sow_id: sowId, title: title.trim(), category: 'delivery', probability: 3, impact: 3, pre_staged: 1 }); setTitle(''); setAdding(false); load(); toast.success('Risk added — PM notified to expand the detail'); }
    catch (e) { toast.error('Failed: ' + e.message); }
  };
  const close = async (id) => { if (!confirm('Close this risk? It will be removed from the register.')) return; try { await api.delete(`risks/${id}`); load(); } catch (e) { toast.error('Failed: ' + e.message); } };
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-bold text-navy uppercase">Open risks {risks ? `(${risks.length})` : ''}</h4>
        <button onClick={() => setAdding(a => !a)} className="text-[8px] font-semibold text-blue-500 hover:underline">{adding ? 'Cancel' : '+ Add'}</button>
      </div>
      {adding && (
        <div className="flex gap-1 mb-2">
          <input className="form-input flex-1 text-[9px]" placeholder="Risk title…" value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} />
          <button onClick={add} className="px-2 py-0.5 text-[9px] font-semibold bg-navy text-white rounded">Add</button>
        </div>
      )}
      {risks === null ? <div className="text-[9px] text-gray-400">Loading…</div>
        : risks.length === 0 ? <div className="text-[9px] text-gray-300">No open risks</div>
        : (
          <ul className="space-y-1">
            {risks.map(r => (
              <li key={r.id} className="flex items-start gap-2 text-[9px]">
                <span className={`mt-0.5 px-1 rounded text-[7px] font-bold ${r.risk_score >= 15 ? 'bg-red-100 text-red-700' : r.risk_score >= 8 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{r.risk_score ?? '–'}</span>
                <span className="flex-1 text-gray-700">{r.title}{r.pre_staged ? <span className="text-amber-500 ml-1" title="Pre-staged — awaiting PM expansion of details">⚠</span> : null}</span>
                <button onClick={() => close(r.id)} className="text-[7px] text-gray-400 hover:text-red-600" title="Close risk">close</button>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

function TasksPanel({ sowId }) {
  const { toast } = useToast();
  const [tasks, setTasks] = useState(null);
  const load = () => api.get(`sows/${sowId}/project-tasks`).then(d => setTasks(Array.isArray(d) ? d : [])).catch(() => setTasks([]));
  useEffect(() => { load(); }, [sowId]);
  const cycle = async (t) => {
    const next = t.status === 'not_started' ? 'in_progress' : t.status === 'in_progress' ? 'completed' : 'not_started';
    try { await api.put(`sows/${sowId}/project-tasks/${t.id}`, { status: next, completion_pct: next === 'completed' ? 100 : next === 'in_progress' ? (t.completion_pct || 50) : 0 }); load(); }
    catch (e) { toast.error('Failed: ' + e.message); }
  };
  const dot = (s) => s === 'completed' ? '#10B981' : s === 'in_progress' ? '#3B82F6' : s === 'blocked' ? '#EF4444' : '#D1D5DB';
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <h4 className="text-[10px] font-bold text-navy uppercase mb-2">Tasks {tasks ? `(${tasks.length})` : ''}</h4>
      {tasks === null ? <div className="text-[9px] text-gray-400">Loading…</div>
        : tasks.length === 0 ? <div className="text-[9px] text-gray-300">No project tasks</div>
        : (
          <ul className="space-y-1">
            {tasks.map(t => (
              <li key={t.id} className="flex items-center gap-2 text-[9px]">
                <button onClick={() => cycle(t)} title={`${t.status} — click to advance`} className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: dot(t.status) }} />
                <span className="flex-1 text-gray-700 truncate">{t.name}</span>
                {t.owner_name && <span className="text-[8px] text-gray-400">{t.owner_name}</span>}
                <span className="text-[8px] text-gray-400 w-8 text-right">{t.completion_pct ? `${t.completion_pct}%` : ''}</span>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function DeliveryCockpitPage() {
  const { currentUser } = useApp();
  const isPmo = useMemo(() => {
    const roles = parseRoles(currentUser?.user_role);
    return roles.includes('pmo') || roles.includes('admin');
  }, [currentUser]);

  const [view, setView] = useState('mine');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSow, setSelectedSow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sowFinance, setSowFinance] = useState(null);
  const [search, setSearch] = useState('');
  const [showAwaitingFinance, setShowAwaitingFinance] = useState(false);
  const [editingProject, setEditingProject] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [depReasons, setDepReasons] = useState([]);
  const [missedReasons, setMissedReasons] = useState([]);

  const loadList = () => {
    setLoading(true);
    api.get('project-updates', { view })
      .then(r => setProjects(Array.isArray(r) ? r : []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  };
  useEffect(loadList, [view]);
  useEffect(() => {
    api.get('config-options', { category: 'dependency_reason' }).then(d => setDepReasons(Array.isArray(d) ? d.filter(o => o.is_active !== 0) : [])).catch(() => {});
    api.get('config-options', { category: 'missed_milestone_reason' }).then(d => setMissedReasons(Array.isArray(d) ? d.filter(o => o.is_active !== 0) : [])).catch(() => {});
  }, []);

  const loadDetail = (sowId) => {
    setDetailLoading(true);
    api.get(`project-updates/${sowId}`).then(setDetail).catch(() => setDetail(null)).finally(() => setDetailLoading(false));
  };

  // Once every milestone on a project is invoiced/paid (etc.) there's nothing
  // left to deliver — only payment is outstanding, which isn't a PMO/PM
  // concern. Split those out so they don't clutter the list or count toward
  // the health tiles; they're still reachable via the toggle below.
  const activeProjects         = useMemo(() => projects.filter(p => !p.awaiting_finance), [projects]);
  const awaitingFinanceProjects = useMemo(() => projects.filter(p => p.awaiting_finance), [projects]);

  // Filter, then rank worst-first so the PMO/PM sees what needs attention first.
  const sortedProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (p) => !q ||
      (p.reference || '').toLowerCase().includes(q) || (p.client_name || '').toLowerCase().includes(q) ||
      (p.owner_name || '').toLowerCase().includes(q) || (p.delivery_owner_name || '').toLowerCase().includes(q);
    const byPriority = (a, b) =>
      (RAG_RANK[b.overall_rag] || 0) - (RAG_RANK[a.overall_rag] || 0) ||
      (b.rag_stale ? 1 : 0) - (a.rag_stale ? 1 : 0) ||
      (b.overdue_milestones || 0) - (a.overdue_milestones || 0) ||
      (a.reference || '').localeCompare(b.reference || '');
    const active = activeProjects.filter(matches).sort(byPriority);
    if (!showAwaitingFinance) return active;
    const done = awaitingFinanceProjects.filter(matches).sort((a, b) => (a.reference || '').localeCompare(b.reference || ''));
    return [...active, ...done];
  }, [activeProjects, awaitingFinanceProjects, search, showAwaitingFinance]);

  // Keep a selection; default to the first (worst) project once loaded.
  useEffect(() => {
    if (!sortedProjects.length) { setSelectedSow(null); return; }
    if (!selectedSow || !sortedProjects.find(p => p.id === selectedSow)) setSelectedSow(sortedProjects[0].id);
  }, [sortedProjects]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load project-update detail (narrative + milestones) and billing detail when selection changes.
  useEffect(() => {
    if (!selectedSow) { setDetail(null); setSowFinance(null); return; }
    loadDetail(selectedSow);
    setSowFinance(null);
    // The billing figures are SOW money, the sow:see-financials grant (pen test
    // AD26341, AD12/AD16): without it the SOW comes back without them, so it is not asked.
    if (!canAccess('sow:see-financials')) return;
    api.get(`sows/${selectedSow}`).then(setSowFinance).catch(() => setSowFinance(null));
  }, [selectedSow]);  // eslint-disable-line react-hooks/exhaustive-deps

  const current = sortedProjects.find(p => p.id === selectedSow);

  const totals = useMemo(() => {
    let red = 0, amber = 0, stale = 0, overdue = 0;
    for (const p of activeProjects) {
      if (p.overall_rag === 'red') red++; else if (p.overall_rag === 'amber') amber++;
      if (p.rag_stale) stale++;
      if ((p.overdue_milestones || 0) > 0) overdue++;
    }
    return { red, amber, stale, overdue, total: activeProjects.length };
  }, [activeProjects]);

  if (loading) return <div className="p-6 text-gray-400 text-sm">Loading delivery cockpit…</div>;

  return (
    <div className="fade-in">
      {editingProject && current && (
        <ProjectStatusModal
          sowId={current.id}
          sowRef={current.reference}
          sow={detail?.sow}
          latest={detail?.latest}
          history={detail?.history}
          onClose={() => setEditingProject(false)}
          onSaved={() => { setEditingProject(false); loadList(); loadDetail(current.id); }}
        />
      )}
      {editingMilestone && detail && (
        <MilestoneModal
          sow={detail.sow}
          milestone={editingMilestone}
          depReasons={depReasons}
          missedReasons={missedReasons}
          isPmo={isPmo}
          onClose={() => setEditingMilestone(null)}
          onSaved={() => { setEditingMilestone(null); loadList(); loadDetail(selectedSow); }}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Delivery Cockpit</h2>
          <div className="text-[9px] text-gray-500 mt-0.5">
            {totals.total} project{totals.total !== 1 ? 's' : ''} · one update per project drives RAG &amp; delivery status — milestones carry their own dates and billing status
            {awaitingFinanceProjects.length > 0 && <> · {awaitingFinanceProjects.length} fully invoiced, awaiting payment only</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded border border-gray-200 overflow-hidden">
            {['mine', 'all'].map(v => (
              <button key={v} onClick={() => setView(v)} className={`px-2.5 py-1 text-[9px] font-semibold ${view === v ? 'bg-navy text-white' : 'bg-white text-gray-500'}`}>{v === 'mine' ? 'My projects' : 'All active'}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {[
          { label: 'Red', val: totals.red, cls: 'text-red-700 bg-red-50 border-red-200' },
          { label: 'Amber', val: totals.amber, cls: 'text-amber-700 bg-amber-50 border-amber-200' },
          { label: 'Overdue milestones', val: totals.overdue, cls: 'text-rose-700 bg-rose-50 border-rose-200' },
          { label: 'Stale / never updated', val: totals.stale, cls: 'text-gray-700 bg-gray-50 border-gray-200' },
        ].map(t => (
          <div key={t.label} className={`px-3 py-2 rounded-lg border ${t.cls}`}>
            <div className="text-lg font-extrabold leading-none">{t.val}</div>
            <div className="text-[8px] font-bold mt-0.5">{t.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Left: project list */}
        <div className="lg:col-span-4 xl:col-span-3">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects…" className="form-input w-full text-[9px] mb-2" />
          {awaitingFinanceProjects.length > 0 && (
            <button onClick={() => setShowAwaitingFinance(v => !v)} className="text-[8px] text-gray-400 hover:text-navy font-semibold mb-2 underline decoration-dotted">
              {showAwaitingFinance ? 'Hide' : 'Show'} {awaitingFinanceProjects.length} fully invoiced (awaiting payment only)
            </button>
          )}
          <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
            {sortedProjects.length === 0 && <div className="text-[9px] text-gray-400 p-2">No projects</div>}
            {sortedProjects.map((p, i) => {
              const ds = dsMeta(p.delivery_status);
              const awaiting = !!p.awaiting_finance;
              const isFirstDone = showAwaitingFinance && awaiting && !sortedProjects[i - 1]?.awaiting_finance;
              return (
                <div key={p.id}>
                  {isFirstDone && (
                    <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wide px-1 pt-2 pb-1">Fully invoiced — awaiting payment only</div>
                  )}
                  <button
                    onClick={() => setSelectedSow(p.id)}
                    className={`w-full text-left p-2 rounded-lg border transition-colors ${selectedSow === p.id ? 'border-navy bg-navy/5' : awaiting ? 'border-gray-200 bg-gray-50 opacity-70 hover:opacity-100' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-1.5">
                      {!awaiting && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.overall_rag ? RAG_DOT[p.overall_rag] : '#E5E7EB' }} title={`RAG: ${p.overall_rag || 'n/a'}`} />}
                      <span className="text-[8px] text-gray-400 truncate">{p.reference}</span>
                      {!awaiting && p.overdue_milestones > 0 && <span className="ml-auto text-[7px] font-bold px-1 rounded-full bg-red-100 text-red-700">{p.overdue_milestones} overdue</span>}
                    </div>
                    <div className={`font-bold text-[10px] truncate ${awaiting ? 'text-gray-500' : 'text-navy'}`}>{p.detail || p.reference}</div>
                    <div className="text-[8px] text-gray-400 truncate">{p.client_name || '—'}{p.po_number ? ` · PO ${p.po_number}` : ''}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {awaiting ? (
                        <span className="text-[7px] px-1.5 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-500">Delivered · awaiting payment</span>
                      ) : (
                        <>
                          <span className={`text-[7px] px-1.5 py-0.5 rounded-full font-semibold ${ds.bg}`}>{ds.label}</span>
                          <span className={`text-[7px] ${p.rag_stale ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                            {p.rag_age_days == null ? 'no update yet' : p.rag_age_days === 0 ? 'updated today' : `updated ${p.rag_age_days}d ago`}
                          </span>
                        </>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: selected project workspace */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-3">
          {!current ? <div className="text-gray-400 text-sm p-6">Select a project.</div> : (
            <>
              {/* Project header + status */}
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-extrabold text-navy">{current.detail || current.reference}</div>
                    <div className="text-[9px] text-gray-400 font-mono">{current.reference}</div>
                    <div className="text-[8px] text-gray-400 mt-0.5">{current.client_name || '—'}{current.po_number ? ` · PO ${current.po_number}` : ''} · Owner: {current.owner_name || '—'}{current.delivery_owner_name ? ` · Delivery: ${current.delivery_owner_name}` : ''}</div>
                  </div>
                  {sowFinance && (
                    <div className="text-right text-[9px] flex-shrink-0">
                      <div className="text-gray-400 text-[8px] uppercase font-bold">Billing</div>
                      <div className="text-gray-700">{formatMoney(sowFinance.invoiced_sum || 0)} / {formatMoney(sowFinance.total_excl_vat || 0)}</div>
                      <div className="text-[8px] text-gray-400">{formatMoney(sowFinance.remaining_to_invoice || 0)} remaining</div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: current.overall_rag ? RAG_DOT[current.overall_rag] : '#E5E7EB' }} />
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${dsMeta(current.delivery_status).bg}`}>{dsMeta(current.delivery_status).label}</span>
                    {detail?.latest?.updated_by_name && (
                      <span className="text-[8px] text-gray-400">by <strong>{detail.latest.updated_by_name}</strong>{detail.latest.week_commencing ? ` · ${formatDate(detail.latest.week_commencing)}` : ''}</span>
                    )}
                  </div>
                  <button onClick={() => setEditingProject(true)} className="px-3 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80">Update project status ›</button>
                </div>

                {detail?.latest?.recent_progress && (
                  <div className="mt-2 text-[8px] text-gray-500 bg-gray-50 rounded px-2 py-1.5">
                    <span className="font-semibold text-gray-400">Progress: </span>{detail.latest.recent_progress}
                  </div>
                )}
                {detail?.latest?.risks_issues && (
                  <div className="mt-1.5 text-[8px] text-red-700 bg-red-50 rounded px-2 py-1.5">
                    <span className="font-semibold">⚠ </span>{detail.latest.risks_issues}
                  </div>
                )}
                {(detail?.sow?.rr_actions || detail?.sow?.rr_comment) && (
                  <div className="mt-1.5 text-[8px] text-gray-600 bg-blue-50/60 rounded px-2 py-1.5 space-y-0.5">
                    {detail.sow.rr_actions && <div><span className="font-semibold text-gray-400">RR actions: </span>{detail.sow.rr_actions}</div>}
                    {detail.sow.rr_comment && <div><span className="font-semibold text-gray-400">RR comment: </span>{detail.sow.rr_comment}</div>}
                  </div>
                )}
              </div>

              {/* Milestones */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b bg-gray-50 text-[10px] font-bold text-navy uppercase">Milestones</div>
                {detailLoading ? <div className="p-4 text-[10px] text-gray-400">Loading…</div> : (
                  <div className="divide-y divide-gray-100">
                    {(detail?.milestones || []).length === 0 && <div className="p-4 text-[10px] text-gray-400">No active milestones on this project.</div>}
                    {(detail?.milestones || []).map(m => {
                      const doneish = isDoneish(m.status);
                      return (
                        <div key={m.id} className="px-3 py-2 flex items-start gap-3 hover:bg-gray-50/60">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-gray-800 text-[10px]">{m.milestone_code || 'Milestone'}</span>
                              <span className={`text-[7px] px-1 py-0.5 rounded uppercase font-semibold ${doneish ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{m.status}</span>
                              {!!m.is_overdue && <span className="text-[7px] font-bold text-red-600 uppercase">overdue</span>}
                            </div>
                            {m.description && <div className="text-[8px] text-gray-400 truncate">{m.description}</div>}
                            {m.summary && <div className="text-[8px] text-gray-500 mt-0.5">{m.summary}</div>}
                            <div className="flex items-center gap-3 mt-1 text-[8px] text-gray-500">
                              <span>Contract {m.contract_date ? formatDate(m.contract_date) : '—'}</span>
                              <span>Forecast {m.forecast_date ? formatDate(m.forecast_date) : '—'}</span>
                              {m.invoice_amount_excl != null && <span>£{Number(m.invoice_amount_excl).toLocaleString()}</span>}
                            </div>
                            {m.dependency_reason && <div className="text-[8px] text-amber-600 mt-0.5">Dependency: {m.dependency_reason}{m.dependency_comment ? ` — ${m.dependency_comment}` : ''}</div>}
                          </div>
                          <button onClick={() => setEditingMilestone(m)} className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded self-center whitespace-nowrap">Update ›</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Risks + tasks */}
              <div className="grid grid-cols-2 gap-3">
                <RisksPanel sowId={current.id} />
                <TasksPanel sowId={current.id} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
